import Document from '@tiptap/extension-document';
import Paragraph from '@tiptap/extension-paragraph';
import Text from '@tiptap/extension-text';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { AnimatePresence, motion } from 'framer-motion';
import { Plus, Sparkles, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useId, useState } from 'react';

import { Button } from '@/components/ui/catalyst/button';
import { cn } from '@/lib/utils';
import { humanizeColumnId } from '@/utils/formatting';

export interface PromptEditorProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  minHeight?: string;
  maxHeight?: string;
  'aria-labelledby'?: string;
}

export const PromptEditor = ({
  value,
  onChange,
  disabled,
  placeholder = 'What should the agent extract?',
  className,
  minHeight = '60px',
  maxHeight,
  'aria-labelledby': ariaLabelledBy,
}: PromptEditorProps) => {
  const editor = useEditor({
    extensions: [
      Document,
      Paragraph,
      Text,
      StarterKit.configure({
        document: false,
        paragraph: false,
        text: false,
        hardBreak: {}, // Enable hard breaks in the editor with default options
      }),
    ],
    content: value,
    editorProps: {
      attributes: {
        class:
          'min-h-[60px] p-2 text-sm focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50',
        'data-placeholder': placeholder,
      },
    },
    editable: !disabled,
    onUpdate: ({ editor: editorInstance }) => {
      onChange(editorInstance.getHTML());
    },
  });

  // Update editor editable state when disabled changes
  useEffect(() => {
    if (editor) {
      editor.setEditable(!disabled);
    }
  }, [disabled, editor]);

  // Add custom styles to the editor when mounted (mirrors MessageInput)
  useEffect(() => {
    if (editor) {
      const editorContent = editor.view.dom as HTMLElement;
      editorContent.style.overflow = 'visible';
      editorContent.style.whiteSpace = 'pre-wrap';
      editorContent.style.minHeight = minHeight;
    }
  }, [editor, minHeight]);

  // Sync external value changes (e.g., from template loading)
  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value);
    }
  }, [value, editor]);

  return (
    <div
      aria-labelledby={ariaLabelledBy}
      className={cn(
        'rounded-lg resize-y overflow-auto',
        'bg-white border border-gray-200 transition-all duration-150',
        'focus-within:border-blue-300 focus-within:ring-2 focus-within:ring-blue-100',
        disabled && 'opacity-50 cursor-not-allowed',
        className,
      )}
      style={{ minHeight, maxHeight }}
    >
      <EditorContent editor={editor} className="h-full" />
    </div>
  );
};

export interface ColumnDefinition {
  id: string;
  prompt: string;
  _key?: string; // Internal stable key for React rendering
  _width?: number; // Internal width for resizing
  _height?: number; // Internal height for resizing
}

interface ColumnDefinitionBuilderProps {
  columns: ColumnDefinition[];
  onChange: (columns: ColumnDefinition[]) => void;
  disabled?: boolean;
}

interface ColumnCardProps {
  column: ColumnDefinition;
  index: number;
  onUpdate: (index: number, field: 'id' | 'prompt', value: string) => void;
  onRemove: (index: number) => void;
  onResize: (index: number, width: number, height: number) => void;
  disabled?: boolean;
}

const DEFAULT_WIDTH = 280;
const MIN_WIDTH = 180;
const MAX_WIDTH = 360;
const DEFAULT_HEIGHT = 300;
const MIN_HEIGHT = 120;
const MAX_HEIGHT = 700;

const ColumnCard = ({
  column,
  index,
  onUpdate,
  onRemove,
  onResize,
  disabled,
}: ColumnCardProps) => {
  const inputId = useId();
  const [isResizing, setIsResizing] = useState(false);
  const width = column._width || DEFAULT_WIDTH;
  const height = column._height || DEFAULT_HEIGHT;

  const handleResizeStart = (
    e: React.MouseEvent,
    direction: 'horizontal' | 'vertical' | 'both',
  ) => {
    e.preventDefault();
    setIsResizing(true);
    const startX = e.clientX;
    const startY = e.clientY;
    const startWidth = width;
    const startHeight = height;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      let newWidth = startWidth;
      let newHeight = startHeight;

      if (direction === 'horizontal' || direction === 'both') {
        const deltaX = moveEvent.clientX - startX;
        newWidth = Math.min(
          MAX_WIDTH,
          Math.max(MIN_WIDTH, startWidth + deltaX),
        );
      }

      if (direction === 'vertical' || direction === 'both') {
        const deltaY = moveEvent.clientY - startY;
        newHeight = Math.min(
          MAX_HEIGHT,
          Math.max(MIN_HEIGHT, startHeight + deltaY),
        );
      }

      onResize(index, newWidth, newHeight);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  return (
    <motion.div
      layout={!isResizing}
      initial={{ opacity: 0, scale: 0.95, x: 20 }}
      animate={{ opacity: 1, scale: 1, x: 0 }}
      exit={{ opacity: 0, scale: 0.95, x: -20 }}
      transition={
        isResizing
          ? { duration: 0 }
          : {
              type: 'spring',
              stiffness: 500,
              damping: 35,
              opacity: { duration: 0.15 },
            }
      }
      style={{ width, height }}
      className={cn(
        'group relative flex-shrink-0',
        'rounded-lg border bg-white',
        'border-slate-200 hover:border-slate-300 hover:shadow-sm',
        disabled && 'opacity-50 pointer-events-none',
        isResizing && 'select-none',
      )}
    >
      {/* Delete Button */}
      <button
        type="button"
        onClick={() => onRemove(index)}
        disabled={disabled}
        className={cn(
          'absolute -top-2 -right-2 z-10',
          'p-1.5 rounded-full',
          'bg-white border border-slate-200 shadow-sm',
          'text-slate-400 hover:text-red-500 hover:border-red-200 hover:bg-red-50',
          'opacity-0 group-hover:opacity-100',
          'transition-all duration-150',
          'focus:outline-none focus:opacity-100',
        )}
        aria-label="Remove column"
      >
        <Trash2 className="w-3.5 h-3.5" />
      </button>

      {/* Horizontal Resize Handle (right edge) */}
      <button
        type="button"
        aria-label="Resize column width"
        onMouseDown={(e) => handleResizeStart(e, 'horizontal')}
        className={cn(
          'absolute right-0 top-0 bottom-3 w-3 cursor-col-resize',
          'flex items-center justify-center',
          'opacity-0 group-hover:opacity-100 transition-opacity',
          'focus:outline-none focus:opacity-100',
          isResizing && 'opacity-100',
        )}
      >
        <span
          className={cn(
            'h-8 w-1 rounded-full transition-colors',
            isResizing ? 'bg-indigo-400' : 'bg-slate-300 hover:bg-slate-400',
          )}
        />
      </button>

      {/* Vertical Resize Handle (bottom edge) */}
      <button
        type="button"
        aria-label="Resize column height"
        onMouseDown={(e) => handleResizeStart(e, 'vertical')}
        className={cn(
          'absolute bottom-0 left-0 right-3 h-3 cursor-row-resize',
          'flex items-center justify-center',
          'opacity-0 group-hover:opacity-100 transition-opacity',
          'focus:outline-none focus:opacity-100',
          isResizing && 'opacity-100',
        )}
      >
        <span
          className={cn(
            'w-8 h-1 rounded-full transition-colors',
            isResizing ? 'bg-indigo-400' : 'bg-slate-300 hover:bg-slate-400',
          )}
        />
      </button>

      {/* Corner Resize Handle (bottom-right) */}
      <button
        type="button"
        aria-label="Resize column"
        onMouseDown={(e) => handleResizeStart(e, 'both')}
        className={cn(
          'absolute bottom-0 right-0 w-3 h-3 cursor-nwse-resize',
          'flex items-center justify-center',
          'opacity-0 group-hover:opacity-100 transition-opacity',
          'focus:outline-none focus:opacity-100',
          isResizing && 'opacity-100',
        )}
      >
        <span
          className={cn(
            'w-2 h-2 rounded-sm transition-colors',
            isResizing ? 'bg-indigo-400' : 'bg-slate-300 hover:bg-slate-400',
          )}
        />
      </button>

      <div className="p-3 space-y-2 pr-4 h-full flex flex-col">
        {/* Column ID */}
        <div className="space-y-1">
          <div
            className="text-sm font-semibold text-slate-900 truncate"
            title={column.id || undefined}
          >
            {column.id ? humanizeColumnId(column.id) : `Column ${index + 1}`}
          </div>
          <span
            id={`${inputId}-id-label`}
            className="text-[10px] font-semibold uppercase tracking-wider text-slate-400"
          >
            Column key
          </span>
          <input
            id={`${inputId}-id`}
            type="text"
            aria-labelledby={`${inputId}-id-label`}
            value={column.id}
            onChange={(e) => onUpdate(index, 'id', e.target.value)}
            placeholder="e.g. client_name"
            disabled={disabled}
            className={cn(
              'w-full px-2 py-1.5 rounded-md text-sm font-mono',
              'bg-slate-50 border border-slate-200 transition-all duration-150',
              'placeholder:text-slate-300',
              'focus:outline-none focus:bg-white focus:border-indigo-300 focus:ring-2 focus:ring-indigo-100',
            )}
          />
        </div>

        {/* Prompt Input */}
        <div className="space-y-1 flex-1 flex flex-col min-h-0">
          <span
            id={`${inputId}-prompt-label`}
            className="text-[10px] font-semibold uppercase tracking-wider text-slate-400"
          >
            Extraction Prompt
          </span>
          <PromptEditor
            aria-labelledby={`${inputId}-prompt-label`}
            value={column.prompt}
            onChange={(value) => onUpdate(index, 'prompt', value)}
            disabled={disabled}
            className="flex-1"
          />
        </div>
      </div>
    </motion.div>
  );
};

export const ColumnDefinitionBuilder = ({
  columns,
  onChange,
  disabled,
}: ColumnDefinitionBuilderProps) => {
  const handleAddColumn = useCallback(() => {
    const stableKey = `col-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    onChange([...columns, { id: '', prompt: '', _key: stableKey }]);
  }, [columns, onChange]);

  const handleUpdateColumn = useCallback(
    (index: number, field: 'id' | 'prompt', value: string) => {
      const updated = [...columns];
      updated[index] = { ...updated[index], [field]: value };
      onChange(updated);
    },
    [columns, onChange],
  );

  const handleRemoveColumn = useCallback(
    (index: number) => {
      onChange(columns.filter((_, i) => i !== index));
    },
    [columns, onChange],
  );

  const handleResizeColumn = useCallback(
    (index: number, width: number, height: number) => {
      const updated = [...columns];
      updated[index] = { ...updated[index], _width: width, _height: height };
      onChange(updated);
    },
    [columns, onChange],
  );

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-zinc-700">
          {columns.length} column{columns.length !== 1 ? 's' : ''} defined
        </p>
        <Button
          type="button"
          color="blue"
          onClick={handleAddColumn}
          disabled={disabled}
          className="!text-xs !px-3 !py-1.5"
        >
          <Plus className="w-3.5 h-3.5 -ml-0.5" />
          Add Column
        </Button>
      </div>

      {/* Horizontal Scrollable Column List */}
      <div className="relative">
        <AnimatePresence mode="popLayout" initial={false}>
          {columns.length === 0 ? (
            <motion.div
              key="empty-state"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              className={cn(
                'relative overflow-hidden',
                'rounded-xl border-2 border-dashed border-slate-200',
                'bg-gradient-to-br from-slate-50 to-slate-100/50',
                'px-4 py-6 text-center',
              )}
            >
              {/* Decorative gradient orbs */}
              <div className="absolute -top-12 -right-12 w-32 h-32 bg-indigo-100 rounded-full blur-3xl opacity-50" />
              <div className="absolute -bottom-8 -left-8 w-24 h-24 bg-violet-100 rounded-full blur-2xl opacity-50" />

              <div className="relative">
                <div className="mx-auto w-10 h-10 rounded-lg bg-white shadow-sm border border-slate-100 flex items-center justify-center mb-3">
                  <Sparkles className="w-5 h-5 text-indigo-500" />
                </div>
                <h4 className="text-sm font-medium text-slate-700 mb-1">
                  No columns defined
                </h4>
                <p className="text-xs text-slate-500 max-w-[260px] mx-auto leading-relaxed">
                  Add columns to define what structured data the agent should
                  extract from conversations.
                </p>
              </div>
            </motion.div>
          ) : (
            <div className="overflow-x-auto pb-2 -mx-1 px-1">
              <div className="flex gap-3 py-1">
                {columns.map((column, index) => (
                  <ColumnCard
                    key={column._key || `fallback-${index}`}
                    column={column}
                    index={index}
                    onUpdate={handleUpdateColumn}
                    onRemove={handleRemoveColumn}
                    onResize={handleResizeColumn}
                    disabled={disabled}
                  />
                ))}
              </div>
            </div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};
